import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, MessageCircle } from "lucide-react";
import BrandSpinner from "@/components/ui/BrandSpinner";
import { usePublicHelpGuide, usePublicHelpHub } from "@/hooks/help-hub/useHelpHub";
import { useHelpHubPresentation } from "@/hooks/help-hub/useHelpHubPresentation";
import { useHelpGuideDownload } from "@/hooks/help-hub/useHelpGuideDownload";
import { hubBrandTheme, inkOn, tint } from "./brandInk";
import { HubFooter, HubHeader, HubNotFound } from "./components/HubChrome";
import { HubGenieStage } from "./components/HubGenieStage";
import { HubGuideArticle } from "./components/HubGuideArticle";
import { HubGuideCompanion } from "./components/HubGuideCompanion";
import "./help-hub.css";

/**
 * One published guide, rendered as a readable page at
 * `/help/:url_name/:documentId`.
 *
 * Serving the text rather than the original file is the whole point — a wall of
 * PDF links is the support page these businesses already failed to build.
 */
const HelpGuidePage = () => {
  const { url_name: urlName, documentId } = useParams<{
    url_name: string;
    documentId: string;
  }>();
  const { data: hub, isLoading: hubLoading } = usePublicHelpHub(urlName);
  const { data: guide, isLoading: guideLoading } = usePublicHelpGuide(
    urlName,
    documentId,
  );
  const { stage, openWithQuestion, openStage, closeStage } = useHelpHubPresentation(hub, `${urlName}/${documentId}`);
  const { isDownloading, download } = useHelpGuideDownload(hub, guide);

  if (hubLoading || guideLoading) {
    return <BrandSpinner label="Opening the guide…" />;
  }

  if (!hub) return <HubNotFound />;

  const { branding } = hub;
  const theme = hubBrandTheme(branding);
  const displayName = branding.brandName || hub.name;
  const hubHref = `/help/${encodeURIComponent(hub.urlName)}`;

  return (
    <div className="genie-support-page flex min-h-[100dvh] flex-col" style={{ ["--hub-primary" as string]: theme.primary }}>
      <Helmet>
        <title>
          {guide ? `${guide.name} — ${displayName}` : `${displayName} — Help Centre`}
        </title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <HubHeader name={hub.name} branding={branding} homeHref={hubHref} wide />

      {/* Full-bleed wash in the customer's colour, so the document sits on
          their brand rather than on generic grey. */}
      <div
        className="flex-1"
        style={{
          background: `linear-gradient(180deg, ${tint(branding.primaryColor, 0.08)} 0%, rgba(255,255,255,0) 520px)`,
        }}
      >
      <main className="hub-page-width hub-guide-page-main">
        <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={hubHref}
          className="hub-guide-back underline-offset-2 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> All guides
        </Link>
        {guide && <button type="button" onClick={() => openWithQuestion(`I have a question about "${guide.name}".`)} className="hub-support-button" style={{ background: theme.gradient, color: inkOn(theme.primary) }}><MessageCircle className="h-4 w-4" /> Ask about this guide</button>}
        </div>

        {!guide ? (
          <div className="mt-8 rounded-2xl border border-genie-border bg-white p-8 text-center">
            <h1 className="text-xl font-bold text-genie-text-primary">
              We couldn't open that guide
            </h1>
            <p className="mt-2 text-[14px] text-genie-text-sub">
              It may have been unpublished, or it's still being prepared. Ask the
              Genie instead — it'll know.
            </p>
            <Link
              to={hubHref}
              className="mt-5 inline-flex min-h-11 items-center rounded-xl px-5 py-2.5 text-[14px] font-semibold"
              style={{ background: theme.gradient, color: inkOn(theme.primary) }}
            >
              Back to the help centre
            </Link>
          </div>
        ) : (
          <div className="hub-guide-reading-layout">
          <article>
            <HubGuideArticle
              guide={guide}
              branding={branding}
              displayName={displayName}
              action={
                guide.genieDocument ? (
                  <button
                    type="button"
                    disabled={isDownloading}
                    onClick={download}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border bg-white px-5 py-2.5 text-[14px] font-semibold transition-shadow hover:shadow-md disabled:opacity-60"
                    style={{
                      borderColor: `${branding.primaryColor}55`,
                      color: branding.primaryColor,
                    }}
                  >
                    <Download className="h-4 w-4" />
                    {isDownloading ? "Preparing the PDF…" : "Download as a branded PDF"}
                  </button>
                ) : undefined
              }
            />

          </article>
          <HubGuideCompanion guide={guide} branding={branding} displayName={displayName} hubHref={hubHref} onAsk={openWithQuestion} onTalk={openStage} />
          </div>
        )}
      </main>
      </div>

      <HubFooter name={displayName} branding={branding} wide />

      {stage && (
        <HubGenieStage
          key={`${hub.genieId}/${documentId}`}
          title={displayName}
          branding={branding}
          question={stage.question}
          onClose={closeStage}
        />
      )}
    </div>
  );
};

export default HelpGuidePage;
